import { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Portal,
  Menu,
  MenuItem,
  ButtonBase,
  Divider,
  Slider,
} from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  ChevronDown,
  RotateCcw,
  Check,
  Sparkles,
  MousePointer2,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTour } from './TourProvider';
import { TOUR_STEPS, DEMO_URL, DEMO_CTA_LABEL } from './steps';
import type { TourFollowUp } from './steps';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 10;
const RAIL_WIDTH = 440;          // width of side-docked card layout.
// Booth pacing: three-beat reveal so standing visitors can absorb
// context, then watch the focus close in.
//   1. OVERVIEW_MS — full-screen view (no spotlight) for orientation.
//   2. TRANSITION_MS — spotlight/mask/ring/backdrop smoothly fade in.
//   3. Remainder of autoplayMs — card + highlight hold for reading.
// Total pre-focus = OVERVIEW_MS + TRANSITION_MS (5s at current values).
const OVERVIEW_MS = 3000;
const TRANSITION_MS = 2000;
// Custom smooth-scroll duration (replaces browser scrollIntoView's
// ~300ms jump). Timed so the scroll finishes well inside the overview
// phase — the viewer sees the page settle, then the focus animation.
const SCROLL_MS = 1800;

const pulse = keyframes`
  0%   { opacity: 0.9; transform: scale(1); }
  50%  { opacity: 0.4;  transform: scale(1.05); }
  100% { opacity: 0.9; transform: scale(1); }
`;

// Cursor click ripple — expands and fades when the simulated cursor
// "lands" on its target.
const clickRipple = keyframes`
  0%   { transform: scale(0.4); opacity: 1; }
  100% { transform: scale(1.6); opacity: 0; }
`;

// Custom scroll tween so the booth audience sees the page glide to the
// target instead of a ~300ms browser jump. Finds the nearest scrollable
// ancestor and animates its scrollTop with ease-in-out-cubic so motion
// starts and ends softly.
// Anchor distance (px) below the viewport top when scrollAnchor='top'.
// ~96px clears the sticky AppBar (~72px) plus a small visual gap so the
// header sits just below it rather than flush. Same value regardless
// of viewport height — that's the point on a 50" monitor.
const TOP_ANCHOR_OFFSET = 96;

function smoothScrollIntoView(
  el: HTMLElement,
  anchor: 'center' | 'top' = 'center',
  durationMs = SCROLL_MS,
): (() => void) | undefined {
  // The page-level scroll container is whichever of <html>/<body> Chrome
  // routes through. Treating either as a normal element and writing to
  // `.scrollTop` is unreliable — on this app some MUI/layout setup makes
  // `documentElement.scrollTop = N` a no-op while `window.scrollTo(0, N)`
  // still works. Always go through window for the document scroller.
  const findScrollParent = (node: HTMLElement): HTMLElement | Window => {
    let n: HTMLElement | null = node.parentElement;
    while (n) {
      if (n === document.documentElement || n === document.body) return window;
      const s = window.getComputedStyle(n);
      const canScroll = /(auto|scroll|overlay)/.test(`${s.overflowY}${s.overflow}`);
      if (canScroll && n.scrollHeight > n.clientHeight) return n;
      n = n.parentElement;
    }
    return window;
  };
  const container = findScrollParent(el);
  const containerTop = container === window
    ? 0
    : (container as HTMLElement).getBoundingClientRect().top;
  const containerHeight = container === window
    ? window.innerHeight
    : (container as HTMLElement).clientHeight;
  const containerScrollTop = container === window
    ? window.scrollY
    : (container as HTMLElement).scrollTop;
  const r = el.getBoundingClientRect();
  const targetScrollTop = anchor === 'top'
    ? containerScrollTop + (r.top - containerTop) - TOP_ANCHOR_OFFSET
    : containerScrollTop + (r.top - containerTop) - (containerHeight / 2) + (r.height / 2);
  const startScrollTop = containerScrollTop;
  const delta = targetScrollTop - startScrollTop;
  if (Math.abs(delta) < 8) return;
  const start = performance.now();
  const ease = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  let cancelled = false;
  let rafId = 0;
  const step = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / durationMs);
    const y = startScrollTop + delta * ease(t);
    if (container === window) window.scrollTo({ top: y, behavior: 'auto' });
    else (container as HTMLElement).scrollTop = y;
    if (t < 1) rafId = requestAnimationFrame(step);
  };
  rafId = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
  };
}

function rectsEqual(a: TargetRect | null, b: TargetRect | null) {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function useTargetRect(
  selector: string | null,
  active: boolean,
  dep: unknown,
  scrollAnchor: 'center' | 'top' = 'center',
): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null);
  const rectRef = useRef<TargetRect | null>(null);
  rectRef.current = rect;

  useLayoutEffect(() => {
    if (!active || !selector) {
      if (rectRef.current !== null) setRect(null);
      return;
    }
    let timer = 0;
    let postScrollTimer = 0;
    let cancelScroll: (() => void) | undefined;
    let attempts = 0;
    let scrolled = false;

    const measure = () => {
      // querySelectorAll so a selector can match multiple elements
      // (e.g. "header, .card:nth-of-type(-n+2)") and we spotlight
      // their union — useful when one logical group of UI is split
      // across siblings that don't share a common DOM ancestor.
      const els = document.querySelectorAll(selector);
      const first = els[0] as HTMLElement | undefined;
      let next: TargetRect | null = null;
      if (first) {
        let minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
        els.forEach((node) => {
          const r = (node as HTMLElement).getBoundingClientRect();
          minLeft = Math.min(minLeft, r.left);
          minTop = Math.min(minTop, r.top);
          maxRight = Math.max(maxRight, r.right);
          maxBottom = Math.max(maxBottom, r.bottom);
        });
        next = {
          top: minTop - PADDING,
          left: minLeft - PADDING,
          width: (maxRight - minLeft) + PADDING * 2,
          height: (maxBottom - minTop) + PADDING * 2,
        };
      }
      const el = first ?? null;
      if (!rectsEqual(rectRef.current, next)) {
        rectRef.current = next;
        setRect(next);
      }
      if (el && !scrolled) {
        const r = el.getBoundingClientRect();
        // 'top' anchor always re-positions the target — that's the
        // whole point on big monitors where the target may already be
        // visible but in the middle. 'center' anchor only fires when
        // out-of-view to avoid pointless small jumps.
        const outOfView = r.top < 80 || r.bottom > window.innerHeight - 40;
        const offAnchor = scrollAnchor === 'top' && Math.abs(r.top - TOP_ANCHOR_OFFSET) > 16;
        if (outOfView || offAnchor) {
          // Cancel any in-flight scroll from the previous step before
          // starting a new one. Without this, fast step transitions
          // can leave overlapping rAF tweens fighting over scroll
          // position — the visible jitter the booth showed after a
          // few autoplay loops.
          cancelScroll?.();
          cancelScroll = smoothScrollIntoView(el, scrollAnchor, SCROLL_MS);
          // A single post-scroll re-measure covers the case where the
          // scroll tween runs on a container that doesn't emit scroll
          // events through the window capture listener. Previously we
          // fired 3 staggered re-measures during the tween, which
          // caused the spotlight ring to visibly jitter. Tracked so
          // cleanup can cancel it — otherwise stale measure() calls
          // fire long after the layout effect has been replaced.
          if (postScrollTimer) window.clearTimeout(postScrollTimer);
          postScrollTimer = window.setTimeout(measure, SCROLL_MS + 80);
        }
        scrolled = true;
      }
      if (!el && attempts < 60) {
        attempts += 1;
        timer = window.setTimeout(measure, 100) as unknown as number;
      }
    };

    measure();
    const onResize = () => measure();
    const onScroll = () => measure();
    // Custom event lets the tour ask for a re-measure without firing
    // the synthetic 'resize' that every MUI component on the page
    // listens to (which causes its own layout work / jitter).
    const onRefresh = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('cc-tour-rect-refresh', onRefresh);

    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(postScrollTimer);
      cancelScroll?.();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('cc-tour-rect-refresh', onRefresh);
    };
    // `dep` deliberately participates so that view changes re-measure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, active, dep, scrollAnchor]);

  return rect;
}

export default function TourOverlay() {
  const {
    isOpen, step, highlight, stepIndex, highlightIndex, totalSteps,
    totalCallouts, calloutOrdinal, isFirst, isLast, next, prev, close, restart, goToStep,
    isPlaying, autoplayMs, setAutoplayMs, play, pause,
  } = useTour();
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const softAccent = theme.palette.primary.light;

  // Follow-up sequence state: a highlight may declare one or more
  // followUps that re-anchor the spotlight, animate a cursor click,
  // and fire triggers — all inside one autoplay window. The single
  // `followUp` field is treated as a one-element chain.
  const followUpChain: TourFollowUp[] = useMemo(() => {
    if (!highlight) return [];
    if (highlight.followUps && highlight.followUps.length > 0) return highlight.followUps;
    if (highlight.followUp) return [highlight.followUp];
    return [];
  }, [highlight]);
  // -1 = no followUp yet, 0..n-1 = that followUp has been activated.
  const [followUpIndex, setFollowUpIndex] = useState(-1);
  const [cursorTarget, setCursorTarget] = useState<{ x: number; y: number } | null>(null);
  const [cursorEntered, setCursorEntered] = useState(false);
  const [cursorClicked, setCursorClicked] = useState(false);

  // Reset the follow-up machine on every step/highlight change.
  useEffect(() => {
    setFollowUpIndex(-1);
    setCursorTarget(null);
    setCursorEntered(false);
    setCursorClicked(false);
  }, [stepIndex, highlightIndex]);

  // Effective spotlight target: walks back from the active followUp
  // index to the most recent one that defines a target, falling back
  // to the highlight's own target. A followUp without a target keeps
  // the spotlight where it is (lets it just fire a trigger / animate
  // a cursor without re-anchoring).
  const effectiveTarget = useMemo(() => {
    for (let i = Math.min(followUpIndex, followUpChain.length - 1); i >= 0; i--) {
      const t = followUpChain[i]?.target;
      if (t) return t;
    }
    return highlight?.target ?? null;
  }, [followUpIndex, followUpChain, highlight]);

  // Same walk-back to find the active scrollAnchor.
  const effectiveScrollAnchor = useMemo<'center' | 'top'>(() => {
    for (let i = Math.min(followUpIndex, followUpChain.length - 1); i >= 0; i--) {
      const a = followUpChain[i]?.scrollAnchor;
      if (a) return a;
    }
    return highlight?.scrollAnchor ?? 'center';
  }, [followUpIndex, followUpChain, highlight]);

  const rect = useTargetRect(
    isOpen ? effectiveTarget : null,
    isOpen,
    `${stepIndex}-${highlightIndex}-${step?.view ?? ''}-${followUpIndex}`,
    effectiveScrollAnchor,
  );

  // Autoplay progress ring — restart each time step changes or playback toggles.
  const effectiveAutoplayMs = step?.autoplayMs ?? autoplayMs;
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    setProgress(0);
    if (!isOpen || !isPlaying) return;
    const start = performance.now();
    let rafId = 0;
    const tick = () => {
      const elapsed = performance.now() - start;
      setProgress(Math.min(1, elapsed / effectiveAutoplayMs));
      if (elapsed < effectiveAutoplayMs) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, isPlaying, effectiveAutoplayMs, stepIndex, highlightIndex]);

  // Two-phase reveal on step change. Phase 'overview' shows the whole
  // screen lightly dimmed (no spotlight) so the booth viewer can orient
  // themselves; phase 'focus' is the normal dim + spotlight reveal.
  // Focus is gated on rect stability — if the spotlight target requires
  // a long scroll to come into view, we wait for the scroll to settle
  // before transitioning out of overview. Otherwise the spotlight
  // would fade in mid-scroll and the cursor sequence would start while
  // the page was still moving.
  const [phase, setPhase] = useState<'overview' | 'focus'>('overview');
  const lastRectChangeRef = useRef(performance.now());
  const rectRef = useRef(rect);
  useEffect(() => { rectRef.current = rect; lastRectChangeRef.current = performance.now(); }, [rect]);
  // Rect-stability buffer. The post-scroll re-measure inside
  // useTargetRect fires at SCROLL_MS+80 (~1880ms after scroll start)
  // and may update the rect a final time if the scrolled-to position
  // doesn't exactly match the last scroll-event sample. 350ms covers
  // that final tick plus normal browser scroll-event jitter.
  const RECT_STABLE_MS = 350;
  useEffect(() => {
    if (!isOpen) return;
    setPhase('overview');
    lastRectChangeRef.current = performance.now();
    let cancelled = false;
    let timer = 0;
    const minOverviewMs = step?.overviewMs ?? OVERVIEW_MS;
    const startTime = performance.now();
    const tryAdvance = () => {
      if (cancelled) return;
      const elapsed = performance.now() - startTime;
      const sinceRectChange = performance.now() - lastRectChangeRef.current;
      const overviewDone = elapsed >= minOverviewMs;
      const hasRect = !!rectRef.current;
      const rectStable = hasRect && sinceRectChange >= RECT_STABLE_MS;
      // If the target hasn't resolved at all, don't block — some
      // steps intentionally point the highlight at an element that
      // mounts later (e.g. step 12 sets the highlight to the
      // questionnaire dialog so the spotlight stays off until the
      // cursor sequence opens that dialog). When `!hasRect`, advance
      // after the configured overview window. When a rect IS present,
      // we still wait for it to stabilize so the spotlight doesn't
      // fade in mid-scroll.
      const noRectAdvance = !hasRect;
      if (overviewDone && (rectStable || noRectAdvance)) {
        setPhase('focus');
        return;
      }
      timer = window.setTimeout(tryAdvance, 100);
    };
    timer = window.setTimeout(tryAdvance, minOverviewMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, stepIndex, highlightIndex, step?.view, step?.overviewMs]);

  // Force a re-measure shortly after each step transition. ensureView
  // is async (its first `await` always yields to a microtask), so any
  // DOM mutations it triggers — e.g. seedCart adding cart items, route
  // changes mounting new pages — happen AFTER useTargetRect's first
  // measure. If that first measure ran while the target wasn't in the
  // DOM yet, the only re-measures would be timer retries (capped) and
  // scroll/resize events. When the page doesn't scroll on entry (e.g.
  // step 8: cart-seeded already on /cart), nothing else nudges
  // useTargetRect to look again. Custom event 'cc-tour-rect-refresh'
  // is heard only by useTargetRect — fires a re-measure without
  // triggering the dozens of MUI components that listen to a real
  // 'resize' event (which caused noticeable layout jitter every step
  // transition).
  // Framework-level — applies to every step the same way, so changes
  // to one step's view/target never affect another step's behaviour.
  useEffect(() => {
    if (!isOpen) return;
    const fire = () => window.dispatchEvent(new Event('cc-tour-rect-refresh'));
    const t1 = window.setTimeout(fire, 250);
    const t2 = window.setTimeout(fire, 700);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isOpen, stepIndex, highlightIndex, step?.view]);

  // Follow-up sequencer. Schedules every followUp in the chain at the
  // start of the focus phase. Each followUp's `afterMs` is absolute
  // (measured from focus start, not cumulative). When `cursor: true`,
  // the cursor flies to the cursorTarget (or current spotlight target
  // if cursorTarget is omitted) over CURSOR_FLIGHT_MS, lands at
  // afterMs, plays a click ripple, then fires the trigger and advances
  // the followUp index. Subsequent flights tween the cursor smoothly
  // from its previous landing point — no need to leave the screen
  // between actions.
  const CURSOR_FLIGHT_MS = 1100;
  const CLICK_RIPPLE_MS = 350;
  useEffect(() => {
    if (!isOpen || phase !== 'focus' || followUpChain.length === 0) return;
    let cancelled = false;
    const timers: number[] = [];

    followUpChain.forEach((fup, idx) => {
      const fireTrigger = () => {
        if (cancelled) return;
        if (fup.trigger) {
          window.dispatchEvent(new CustomEvent('cc-tour-trigger', {
            detail: { trigger: fup.trigger, stepId: step?.id },
          }));
        }
        // Advance to this followUp index — the spotlight target will
        // resolve via the walk-back in `effectiveTarget`. Use a fn
        // updater so concurrent followUps don't clobber each other.
        setFollowUpIndex((prev) => Math.max(prev, idx));
      };

      if (fup.cursor) {
        const flightStart = Math.max(0, fup.afterMs - CURSOR_FLIGHT_MS);
        // Phase A: place cursor at landing point. Read rect at flight
        // time so we land on the right pixel even if the layout
        // settled late. Cursor selector defaults to the current
        // effective spotlight target if `cursorTarget` is omitted.
        timers.push(window.setTimeout(() => {
          if (cancelled) return;
          // Resolve where the cursor should land. Priority:
          //   1. explicit cursorTarget on this followUp
          //   2. previous followUp's target (most recent in chain
          //      BEFORE this one — that's the currently-spotlighted
          //      element the cursor should appear to "click")
          //   3. highlight's initial target
          // NOTE: do NOT fall back to fup.target — that's the spotlight
          // *after* this followUp activates, often a modal/dialog that
          // doesn't exist in the DOM yet at flight time. Using it would
          // fail querySelector and abort the cursor animation.
          let cursorSelector = fup.cursorTarget;
          if (!cursorSelector) {
            for (let i = idx - 1; i >= 0; i--) {
              if (followUpChain[i]?.target) {
                cursorSelector = followUpChain[i].target;
                break;
              }
            }
            if (!cursorSelector) cursorSelector = highlight?.target;
          }
          const el = cursorSelector
            ? (document.querySelector(cursorSelector) as HTMLElement | null)
            : null;
          if (!el) {
            fireTrigger();
            return;
          }
          const r = el.getBoundingClientRect();
          const offsetX = fup.cursorOffset?.x ?? 0;
          const offsetY = fup.cursorOffset?.y ?? 0;
          const targetPos = {
            x: r.left + r.width / 2 + offsetX,
            y: r.top + r.height / 2 + offsetY,
          };
          if (fup.cursorRespawn) {
            // Hide the cursor (unmount), then re-render off-screen,
            // then transition to target. Otherwise CSS would tween
            // the cursor from its previous landing position which
            // may now be on stale layout (e.g. after a scroll).
            setCursorTarget(null);
            setCursorEntered(false);
            requestAnimationFrame(() => {
              if (cancelled) return;
              setCursorTarget(targetPos);
              requestAnimationFrame(() => requestAnimationFrame(() => {
                if (!cancelled) setCursorEntered(true);
              }));
            });
          } else {
            setCursorTarget(targetPos);
            // Force a frame so the off-screen → target transition runs
            // on the first cursor flight (when cursorEntered was false).
            requestAnimationFrame(() => requestAnimationFrame(() => {
              if (!cancelled) setCursorEntered(true);
            }));
          }
        }, flightStart));
        // Phase B: click ripple at landing.
        timers.push(window.setTimeout(() => {
          if (!cancelled) setCursorClicked(true);
          // Reset the click state shortly after so the next ripple
          // animates fresh. CSS keyframes restart on remount via the
          // ripple element's keyed render — toggling here is safe.
        }, fup.afterMs));
        timers.push(window.setTimeout(() => {
          if (!cancelled) setCursorClicked(false);
        }, fup.afterMs + CLICK_RIPPLE_MS + 50));
        // Phase C: fire trigger after ripple, advance index.
        timers.push(window.setTimeout(fireTrigger, fup.afterMs + CLICK_RIPPLE_MS));
      } else {
        timers.push(window.setTimeout(fireTrigger, fup.afterMs));
      }
    });

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
    };
  // followUpChain identity changes on every highlight; that's the
  // correct trigger for re-scheduling.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phase, stepIndex, highlightIndex, step?.id]);

  // Publish the bottom-rail footprint as a CSS variable so tour-aware
  // components (e.g. centred MUI dialogs) can shift up to remain
  // optically centred above the rail. Measured dynamically via a ref
  // + ResizeObserver — using the static `max(220px, 34vh)` over-counts
  // when the rail is shorter than its max bound (rail height depends
  // on content, not just the bounds). Hook must run before the early
  // return so hook order stays stable across closed/open states.
  const isBottomCardLayout = step?.cardLayout === 'bottom';
  const railRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!isOpen || !isBottomCardLayout) {
      root.style.removeProperty('--tour-bottom-rail-height');
      return;
    }
    const update = () => {
      const h = railRef.current?.getBoundingClientRect().height ?? 0;
      if (h > 0) {
        root.style.setProperty('--tour-bottom-rail-height', `${Math.round(h)}px`);
      }
    };
    update();
    let ro: ResizeObserver | null = null;
    if (railRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(railRef.current);
    }
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
      root.style.removeProperty('--tour-bottom-rail-height');
    };
  }, [isOpen, isBottomCardLayout, stepIndex, highlightIndex]);

  if (!isOpen || !step || !highlight) return null;

  const shape = highlight.shape ?? 'rect';
  const maskId = 'tour-mask';
  const totalInStep = step.highlights.length;

  // Cards always dock to an edge — never floating. The chosen side
  // comes straight from the step's `cardLayout`, with 'bottom' as the
  // default. There is no viewport-based auto-pick: each step keeps the
  // side it was configured with, regardless of browser width or the
  // location of the highlight target.
  // 'top' uses the same horizontal layout as 'bottom' (it just docks
  // to the top edge instead) so the existing isBottom branches handle
  // typography/sizing/etc. for top too — see `isBottom` below.
  const layoutMode: 'left' | 'right' | 'bottom' | 'top' | 'hero' = step.cardLayout ?? 'bottom';
  const isHero = layoutMode === 'hero';
  // For the rail-card path, treat 'hero' as a no-op — the railSide
  // value isn't read in that branch, but keep it typed so the
  // existing rail rendering doesn't break.
  const railSide: 'left' | 'right' | 'bottom' | 'top' = isHero ? 'bottom' : layoutMode;
  const isTop = railSide === 'top';
  const isBottom = railSide === 'bottom' || isTop;

  // Two-phase dim: lighter during overview, full during focus.
  // Spotlight/ring/mask are rendered whenever rect is known but their
  // opacity is animated by phase so the reveal is a smooth crossfade
  // over TRANSITION_MS, not an instant pop.
  // When there's no target to highlight (rect === null — e.g. target
  // hidden or selector missing), skip the dim entirely so the booth
  // audience keeps seeing the full screen with only the callout card.
  const hasRect = rect !== null;
  // Overview beat now has zero dim — the previous "light dim" (0.32)
  // was a leftover translucent rectangle once the spotlight snapped
  // off, which read as broken. Dim only appears when the spotlight
  // does, so they fade in together when entering focus.
  const backdropAlpha = hasRect && phase === 'focus' ? 0.72 : 0;
  const spotlightVisible = phase === 'focus' ? 1 : 0;
  // Asymmetric transition: fade IN over TRANSITION_MS when entering focus,
  // but snap OUT instantly when leaving (step change → 'overview'). The
  // browser reads the new `transition` value alongside the new property
  // value in the same render, so dropping it to 'none' on the
  // focus→overview frame makes the spotlight/dim disappear immediately
  // — the bare next screen is visible for the full overview beat instead
  // of bleeding into a 2s dim-out animation.
  const fadeMs = phase === 'focus' ? TRANSITION_MS : 0;

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          pointerEvents: 'auto',
        }}
      >
        {/* Dim + spotlight. Two-phase reveal:
            Phase 1 (overview, ~0.9s): light dim, no spotlight — booth viewer
            takes in the whole screen.
            Phase 2 (focus): full dim + spotlight fades in on target. */}
        <svg
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0, display: 'block' }}
        >
          <defs>
            <mask id={maskId}>
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {hasRect && rect &&
                (shape === 'circle' ? (
                  <ellipse
                    cx={rect.left + rect.width / 2}
                    cy={rect.top + rect.height / 2}
                    rx={Math.max(rect.width, rect.height) / 2 + 4}
                    ry={Math.max(rect.width, rect.height) / 2 + 4}
                    fill="black"
                    fillOpacity={spotlightVisible}
                    style={{ transition: fadeMs ? `fill-opacity ${fadeMs}ms ease` : 'none' }}
                  />
                ) : (
                  <rect
                    x={rect.left}
                    y={rect.top}
                    width={rect.width}
                    height={rect.height}
                    rx={10}
                    ry={10}
                    fill="black"
                    fillOpacity={spotlightVisible}
                    style={{ transition: fadeMs ? `fill-opacity ${fadeMs}ms ease` : 'none' }}
                  />
                ))}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={`rgba(6,15,12,${backdropAlpha})`}
            mask={`url(#${maskId})`}
            onClick={close}
            style={{ transition: fadeMs ? `fill ${fadeMs}ms ease` : 'none' }}
          />

          {/* Accent ring + pulse on spotlight — fades in over TRANSITION_MS
              so the ring appears with the same rhythm as the mask hole. */}
          {hasRect && rect && (
            <g
              style={{
                pointerEvents: 'none',
                opacity: spotlightVisible,
                transition: fadeMs ? `opacity ${fadeMs}ms ease` : 'none',
              }}
            >
              {shape === 'circle' ? (
                <>
                  <ellipse
                    cx={rect.left + rect.width / 2}
                    cy={rect.top + rect.height / 2}
                    rx={Math.max(rect.width, rect.height) / 2 + 4}
                    ry={Math.max(rect.width, rect.height) / 2 + 4}
                    fill="none"
                    stroke={accent}
                    strokeWidth="2.5"
                  />
                  <ellipse
                    cx={rect.left + rect.width / 2}
                    cy={rect.top + rect.height / 2}
                    rx={Math.max(rect.width, rect.height) / 2 + 12}
                    ry={Math.max(rect.width, rect.height) / 2 + 12}
                    fill="none"
                    stroke={softAccent}
                    strokeWidth="1.5"
                    opacity="0.55"
                    style={{
                      animation: `${pulse} 2s ease-in-out infinite`,
                      transformOrigin: `${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px`,
                    }}
                  />
                </>
              ) : (
                <>
                  <rect
                    x={rect.left}
                    y={rect.top}
                    width={rect.width}
                    height={rect.height}
                    rx={10}
                    ry={10}
                    fill="none"
                    stroke={accent}
                    strokeWidth="2.5"
                  />
                  <rect
                    x={rect.left - 6}
                    y={rect.top - 6}
                    width={rect.width + 12}
                    height={rect.height + 12}
                    rx={14}
                    ry={14}
                    fill="none"
                    stroke={softAccent}
                    strokeWidth="1.5"
                    opacity="0.55"
                    style={{
                      animation: `${pulse} 2s ease-in-out infinite`,
                      transformOrigin: `${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px`,
                    }}
                  />
                </>
              )}
            </g>
          )}
        </svg>

        {/* Simulated mouse cursor for follow-up "click" sequences.
            Renders only while the followUp cursor animation is in
            flight; disappears once the trigger fires and the spotlight
            switches to the followUp target. */}
        {cursorTarget && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: 44,
              height: 44,
              pointerEvents: 'none',
              // Above the dim/spotlight (zIndex 2000) and the rail
              // card, but below MUI dialogs (zIndex 1300 by default —
              // wait, dialog wins because we want spotlight follow,
              // not above it). The cursor is only visible BEFORE the
              // dialog opens, so layering order doesn't matter much.
              zIndex: 2100,
              // Off-screen until cursorEntered flips, then transition
              // to the click target. Bottom-right entry feels natural
              // for a presenter standing to the right of the screen.
              transform: cursorEntered
                ? `translate(${cursorTarget.x - 8}px, ${cursorTarget.y - 6}px)`
                : `translate(${(typeof window !== 'undefined' ? window.innerWidth : 1280) + 60}px, ${(typeof window !== 'undefined' ? window.innerHeight : 800) + 60}px)`,
              transition: `transform ${CURSOR_FLIGHT_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
              filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.55))',
            }}
          >
            <MousePointer2
              size={40}
              color="#0A1612"
              fill="#ffffff"
              strokeWidth={1.75}
            />
            {cursorClicked && (
              <Box
                sx={{
                  position: 'absolute',
                  left: -14,
                  top: -10,
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  border: `3px solid ${accent}`,
                  animation: `${clickRipple} ${CLICK_RIPPLE_MS}ms ease-out forwards`,
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
        )}

        {/* Info card — always docked to left, right, or bottom edge so
            the card never floats over the page. Bottom rail uses a
            horizontal three-column layout (QR | content | controls)
            so it stays short — sides keep the tall vertical stack.
            'hero' mode skips this rail entirely and renders a
            centered fullscreen closer below. */}
        {!isHero && <Box
          ref={isBottom ? railRef : undefined}
          sx={{
            position: 'absolute',
            ...(railSide === 'left'
              ? { top: 0, bottom: 0, left: 0, width: RAIL_WIDTH, borderRight: `1px solid ${alpha(softAccent, 0.3)}` }
              : railSide === 'right'
                ? { top: 0, bottom: 0, right: 0, width: RAIL_WIDTH, borderLeft: `1px solid ${alpha(softAccent, 0.3)}` }
                : isTop
                  ? { left: 0, right: 0, top: 0, maxHeight: '34vh', minHeight: 220, borderBottom: `1px solid ${alpha(softAccent, 0.3)}` }
                  : { left: 0, right: 0, bottom: 0, maxHeight: '34vh', minHeight: 220, borderTop: `1px solid ${alpha(softAccent, 0.3)}` }),
            overflow: 'hidden',
            // Frosted-glass panel: translucent base + blur so the page
            // behind the rail stays partially visible at every phase.
            // Opacity itself is pinned to 1 (no fade with the reveal)
            // so transparency comes from the rgba bg, not from the
            // element opacity — keeps text crisp and readable.
            // `transform: translateZ(0)` + `willChange: transform` push
            // the rail onto its own GPU compositing layer so page
            // scrolls don't trigger a backdrop-filter repaint, which
            // was causing the bottom rail to glitch on scroll.
            bgcolor: 'rgba(10, 22, 18, 0.82)',
            backdropFilter: 'blur(14px) saturate(140%)',
            WebkitBackdropFilter: 'blur(14px) saturate(140%)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            color: '#fff',
            boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(() => {
            // ── Reusable content blocks (rendered in different
            //    arrangements depending on rail orientation) ──────────
            const headerStrip = (
              <Box
                sx={{
                  userSelect: 'none',
                  bgcolor: alpha('#fff', 0.02),
                  borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
                  px: 1.75,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: alpha('#fff', 0.6), textTransform: 'uppercase' }}>
                  Autoplay demo
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 11, color: alpha('#fff', 0.55), fontWeight: 600 }}>
                  {calloutOrdinal} / {totalCallouts}
                </Typography>
                <IconButton
                  onClick={close}
                  size="small"
                  sx={{ color: alpha('#fff', 0.7), ml: 0.5, '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) } }}
                >
                  <X size={16} />
                </IconButton>
              </Box>
            );

            const progressStripe = (
              <Box sx={{ height: 3, bgcolor: alpha(accent, 0.2), position: 'relative', flexShrink: 0 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: `${(isPlaying ? progress : 0) * 100}%`,
                    bgcolor: accent,
                    transition: isPlaying ? 'none' : 'width 160ms ease',
                  }}
                />
              </Box>
            );

            const tourLabelRow = (
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: isBottom ? 1 : 2 }}>
                <Box
                  sx={{
                    minWidth: isBottom ? 44 : 52,
                    height: isBottom ? 36 : 42,
                    px: 1.25,
                    borderRadius: 1.25,
                    bgcolor: accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: isBottom ? 15 : 17,
                  }}
                >
                  {step.number}
                </Box>
                <Stack spacing={0.35}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 3, color: softAccent, textTransform: 'uppercase' }}>
                    <Sparkles size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                    ClearCollect · Feature tour
                  </Typography>
                  <ButtonBase
                    onClick={(e) => setPickerAnchor(e.currentTarget)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.9,
                      py: 0.3,
                      ml: -0.9,
                      borderRadius: 0.75,
                      color: alpha('#fff', 0.78),
                      '&:hover': { bgcolor: alpha('#fff', 0.08), color: '#fff' },
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                      Feature {stepIndex + 1} of {totalSteps}
                      {totalInStep > 1 && ` · Callout ${highlightIndex + 1} of ${totalInStep}`}
                    </Typography>
                    <ChevronDown size={13} />
                  </ButtonBase>
                </Stack>
              </Stack>
            );

            const titleBlock = (
              <Typography
                sx={{
                  fontSize: isBottom ? 22 : 26,
                  fontWeight: 800,
                  mb: 1,
                  letterSpacing: '-0.4px',
                  lineHeight: 1.15,
                }}
              >
                {step.title}
              </Typography>
            );

            const taglineBlock = (
              <Typography
                sx={{
                  fontSize: isBottom ? 14 : 16,
                  color: softAccent,
                  fontStyle: 'italic',
                  fontWeight: 600,
                  mb: isBottom ? 1.25 : 2,
                  lineHeight: 1.4,
                }}
              >
                {step.tagline}
              </Typography>
            );

            const calloutBlock = (
              <Box
                sx={{
                  bgcolor: alpha(accent, 0.12),
                  border: `1px solid ${alpha(accent, 0.32)}`,
                  borderRadius: 1.25,
                  p: isBottom ? 1.25 : 1.75,
                  mb: isBottom ? 1.25 : 2,
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    mt: 0.2,
                    minWidth: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: accent,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {totalInStep > 1 ? highlightIndex + 1 : '✦'}
                </Box>
                <Typography sx={{ fontSize: isBottom ? 14 : 15, fontWeight: 500, color: alpha('#fff', 0.95), lineHeight: 1.45 }}>
                  {highlight.label}
                </Typography>
              </Box>
            );

            const descriptionBlock = (
              <Typography
                sx={{
                  fontSize: isBottom ? 13.5 : 15,
                  color: alpha('#fff', 0.78),
                  lineHeight: 1.55,
                  mb: step.presenterCue ? 1.25 : (isBottom ? 0 : 2.25),
                }}
              >
                {step.description}
              </Typography>
            );

            const presenterCueBlock = step.presenterCue ? (
              <Box
                sx={{
                  bgcolor: alpha('#fff', 0.04),
                  borderLeft: `3px solid ${alpha(softAccent, 0.6)}`,
                  borderRadius: 0.75,
                  px: 1.75,
                  py: 1.25,
                  mb: isBottom ? 0 : 2.25,
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.6, color: softAccent, textTransform: 'uppercase', mb: 0.5 }}>
                  Presenter cue
                </Typography>
                <Typography sx={{ fontSize: 13, color: alpha('#fff', 0.85), lineHeight: 1.5 }}>
                  {step.presenterCue}
                </Typography>
              </Box>
            ) : null;

            const overallProgressBar = (
              <Box sx={{ height: 3, bgcolor: alpha('#fff', 0.08), borderRadius: 2, mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${(calloutOrdinal / totalCallouts) * 100}%`,
                    bgcolor: softAccent,
                    transition: 'width 240ms ease',
                  }}
                />
              </Box>
            );

            const transportButtons = (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <IconButton
                  onClick={prev}
                  disabled={isFirst}
                  sx={{
                    color: alpha('#fff', 0.8),
                    width: 40,
                    height: 40,
                    '&:hover': { bgcolor: alpha('#fff', 0.08), color: '#fff' },
                    '&.Mui-disabled': { color: alpha('#fff', 0.25) },
                  }}
                >
                  <ChevronLeft size={20} />
                </IconButton>
                <IconButton
                  onClick={() => (isPlaying ? pause() : play())}
                  sx={{
                    color: '#fff',
                    bgcolor: isPlaying ? alpha(accent, 0.3) : alpha(accent, 0.7),
                    border: `1px solid ${alpha(accent, 0.6)}`,
                    width: 40,
                    height: 40,
                    '&:hover': { bgcolor: isPlaying ? alpha(accent, 0.4) : accent },
                  }}
                >
                  {isPlaying ? <Pause size={16} fill="#fff" /> : <Play size={16} fill="#fff" />}
                </IconButton>
                <IconButton
                  onClick={next}
                  disabled={isLast && !isPlaying}
                  sx={{
                    color: alpha('#fff', 0.8),
                    width: 40,
                    height: 40,
                    '&:hover': { bgcolor: alpha('#fff', 0.08), color: '#fff' },
                    '&.Mui-disabled': { color: alpha('#fff', 0.25) },
                  }}
                >
                  <ChevronRight size={20} />
                </IconButton>
                <IconButton
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  sx={{
                    color: alpha('#fff', 0.65),
                    height: 40,
                    minWidth: 48,
                    '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) },
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, px: 0.75 }}>
                    {(autoplayMs / 1000).toFixed(0)}s
                  </Typography>
                </IconButton>
              </Stack>
            );

// Side rails make the QR a hero element — stacked over the
            // label, sized to fill most of the rail width — so booth
            // visitors can scan from a few feet away. Bottom rail keeps
            // the same vertical stack but at a smaller size.
            const qrSize = isBottom ? 132 : 240;
            const qrBlock = (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  overflow: 'hidden',
                  ...(isBottom
                    ? { px: 2, py: 1.75, flexShrink: 0, width: 240, borderRight: `1px solid ${alpha('#fff', 0.08)}` }
                    : { px: 2.5, py: 2.5, borderTop: `1px solid ${alpha('#fff', 0.08)}`, bgcolor: alpha('#fff', 0.03) }),
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    bgcolor: '#fff',
                    borderRadius: 1.5,
                    display: 'flex',
                    flexShrink: 0,
                  }}
                >
                  <QRCodeSVG value={DEMO_URL} size={qrSize} bgColor="#ffffff" fgColor="#0A1612" level="M" />
                </Box>
                <Stack
                  spacing={0.25}
                  sx={{
                    minWidth: 0,
                    textAlign: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      color: softAccent,
                      textTransform: 'uppercase',
                    }}
                  >
                    {DEMO_CTA_LABEL}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: isBottom ? 13 : 15,
                      fontWeight: 700,
                      color: '#fff',
                      lineHeight: 1.2,
                    }}
                  >
                    demo.clearcollectforschools.com
                  </Typography>
                </Stack>
              </Box>
            );

            // ── Layout selector ─────────────────────────────────────
            if (isBottom) {
              return (
                <>
                  {progressStripe}
                  <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {qrBlock}
                    <Box sx={{ flex: 1, minWidth: 0, px: 3, py: 2, overflowY: 'auto' }}>
                      {tourLabelRow}
                      {titleBlock}
                      {taglineBlock}
                      {calloutBlock}
                      {descriptionBlock}
                      {presenterCueBlock}
                    </Box>
                    <Stack
                      sx={{
                        width: 260,
                        flexShrink: 0,
                        borderLeft: `1px solid ${alpha('#fff', 0.08)}`,
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      {/* Top row: callout count (left) + close (right) */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            color: alpha('#fff', 0.55),
                            textTransform: 'uppercase',
                          }}
                        >
                          {calloutOrdinal} / {totalCallouts}
                        </Typography>
                        <IconButton
                          onClick={close}
                          size="small"
                          sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) } }}
                        >
                          <X size={16} />
                        </IconButton>
                      </Stack>

                      {/* Center: transport row, vertically centered in the column */}
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {transportButtons}
                      </Box>

                      {/* Bottom: primary CTA full width */}
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={isLast ? restart : next}
                        endIcon={isLast ? <RotateCcw size={16} /> : <ChevronRight size={16} />}
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          py: 1.1,
                          bgcolor: accent,
                          color: '#fff',
                          '&:hover': { bgcolor: theme.palette.primary.dark },
                        }}
                      >
                        {isLast ? 'Restart' : highlightIndex < totalInStep - 1 ? 'Next' : 'Next feature'}
                      </Button>
                    </Stack>
                  </Box>
                </>
              );
            }

            return (
              <>
                {headerStrip}
                {progressStripe}
                {/* Scrollable description column — only the long-form
                    text scrolls if it overflows. Controls + QR are
                    pinned below so the primary CTA is always visible. */}
                <Box sx={{ px: 3, pt: 3, pb: 1.5, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                  {tourLabelRow}
                  {titleBlock}
                  {taglineBlock}
                  {calloutBlock}
                  {descriptionBlock}
                  {presenterCueBlock}
                </Box>
                {/* Controls strip — pinned. Transport + Close on row
                    one, Next feature on its own line as primary CTA. */}
                <Box sx={{ px: 3, pt: 1.5, pb: 1.5, borderTop: `1px solid ${alpha('#fff', 0.06)}`, flexShrink: 0 }}>
                  {overallProgressBar}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={isLast ? restart : next}
                    endIcon={isLast ? <RotateCcw size={16} /> : <ChevronRight size={16} />}
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      py: 1.1,
                      mb: 1.5,
                      bgcolor: accent,
                      color: '#fff',
                      '&:hover': { bgcolor: theme.palette.primary.dark },
                    }}
                  >
                    {isLast ? 'Restart' : highlightIndex < totalInStep - 1 ? 'Next' : 'Next feature'}
                  </Button>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {transportButtons}
                    <Button
                      onClick={close}
                      sx={{
                        color: alpha('#fff', 0.7),
                        fontSize: 13,
                        fontWeight: 600,
                        '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.06) },
                      }}
                    >
                      Close
                    </Button>
                  </Stack>
                </Box>
                {qrBlock}
              </>
            );
          })()}
        </Box>}

        {/* Hero closer — centered, larger, custom design for the
            final pricing/sales-pitch step. No rail; no spotlight. */}
        {isHero && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              pointerEvents: 'auto',
            }}
          >
            <Box
              sx={{
                width: 'min(820px, 92vw)',
                bgcolor: 'rgba(10, 22, 18, 0.94)',
                backdropFilter: 'blur(18px) saturate(140%)',
                WebkitBackdropFilter: 'blur(18px) saturate(140%)',
                color: '#fff',
                borderRadius: 3,
                border: `1px solid ${alpha(softAccent, 0.25)}`,
                boxShadow: '0 40px 120px rgba(0,0,0,0.6)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {/* Close X */}
              <IconButton
                onClick={close}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  color: alpha('#fff', 0.6),
                  '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) },
                  zIndex: 1,
                }}
              >
                <X size={18} />
              </IconButton>

              {/* Eyebrow + title */}
              <Box sx={{ px: { xs: 4, sm: 6 }, pt: { xs: 5, sm: 6 }, pb: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 3,
                    color: softAccent,
                    textTransform: 'uppercase',
                    mb: 1.5,
                  }}
                >
                  <Sparkles size={12} style={{ marginRight: 6, verticalAlign: -1 }} />
                  ClearCollect · The bottom line
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 28, sm: 36 },
                    fontWeight: 800,
                    letterSpacing: -0.5,
                    lineHeight: 1.1,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontStyle: 'italic',
                    color: softAccent,
                    fontSize: { xs: 16, sm: 18 },
                    mt: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  {step.tagline}
                </Typography>
              </Box>

              {/* Three-tier pricing — mirrors the brochure: Year One
                  Rate (conference special) · Standard Rate · Setup. */}
              <Box
                sx={{
                  mx: { xs: 4, sm: 6 },
                  my: 3,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 1.5,
                }}
              >
                {[
                  {
                    eyebrow: 'Year One Rate',
                    primary: '2.9%',
                    secondary: '+ $0.30',
                    note: 'per transaction · conference special',
                    highlight: true,
                  },
                  {
                    eyebrow: 'Standard Rate',
                    primary: '3.39%',
                    secondary: '+ $0.30',
                    note: 'per transaction · after year one',
                    highlight: false,
                  },
                  {
                    eyebrow: 'Setup & Hosting',
                    primary: 'One-Time',
                    secondary: '',
                    note: 'no recurring subscription',
                    highlight: false,
                  },
                ].map((tier) => (
                  <Box
                    key={tier.eyebrow}
                    sx={{
                      px: 2.25,
                      py: 2.25,
                      borderRadius: 2,
                      bgcolor: tier.highlight ? alpha(accent, 0.22) : alpha('#fff', 0.04),
                      border: `1px solid ${tier.highlight ? alpha(accent, 0.55) : alpha('#fff', 0.1)}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: tier.highlight ? softAccent : alpha('#fff', 0.55),
                      }}
                    >
                      {tier.eyebrow}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 26, sm: 30 },
                          fontWeight: 800,
                          color: '#fff',
                          letterSpacing: -0.5,
                          lineHeight: 1,
                        }}
                      >
                        {tier.primary}
                      </Typography>
                      {tier.secondary && (
                        <Typography
                          sx={{
                            fontSize: { xs: 16, sm: 18 },
                            fontWeight: 600,
                            color: alpha('#fff', 0.85),
                            lineHeight: 1,
                          }}
                        >
                          {tier.secondary}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: alpha('#fff', 0.6),
                        lineHeight: 1.35,
                        mt: 0.25,
                      }}
                    >
                      {tier.note}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Promises — culled from the brochure copy. */}
              <Box sx={{ px: { xs: 4, sm: 6 }, pb: 3 }}>
                <Stack spacing={1.25}>
                  {[
                    'Built for districts processing $300,000+ in fees, activities, fines, and payments annually.',
                    'One platform for all K-12 payments — concessions, event tickets, course fees, registration, activity passes, bus passes.',
                    'No "tip" prompts, no per-family caps, no revenue sharing with the school. Stripe rails, no hidden markups.',
                    'Seamless ERP / SIS integration with nightly data sync. Role-based access for finance, admins, and activity coordinators.',
                  ].map((line) => (
                    <Stack key={line} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          mt: 0.7,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: accent,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: alpha('#fff', 0.92), lineHeight: 1.55 }}>
                        {line}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* Footer: QR + actions */}
              <Box
                sx={{
                  borderTop: `1px solid ${alpha('#fff', 0.08)}`,
                  bgcolor: alpha('#fff', 0.02),
                  px: { xs: 4, sm: 6 },
                  py: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    bgcolor: '#fff',
                    p: 1,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    lineHeight: 0,
                  }}
                >
                  <QRCodeSVG value={DEMO_URL} size={108} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: softAccent, textTransform: 'uppercase', mb: 0.5 }}>
                    {DEMO_CTA_LABEL}
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#fff', mb: 0.5 }}>
                    {DEMO_URL.replace(/^https?:\/\//, '')}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: alpha('#fff', 0.6), lineHeight: 1.4 }}>
                    Scan from your phone, or have us walk your district through it on a 20-minute demo call.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    onClick={restart}
                    startIcon={<RotateCcw size={16} />}
                    sx={{
                      borderColor: alpha('#fff', 0.3),
                      color: '#fff',
                      '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) },
                    }}
                  >
                    Restart tour
                  </Button>
                  <Button
                    variant="contained"
                    onClick={close}
                    sx={{
                      bgcolor: accent,
                      color: '#0A1612',
                      fontWeight: 700,
                      '&:hover': { bgcolor: softAccent },
                    }}
                  >
                    Done
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        )}

        {/* Autoplay interval settings menu */}
        <Menu
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: 2500 }}
          slotProps={{
            root: { sx: { zIndex: 2500 } },
            paper: {
              sx: {
                bgcolor: '#0A1612',
                color: '#fff',
                border: `1px solid ${alpha(softAccent, 0.3)}`,
                borderRadius: 1.5,
                minWidth: 260,
                p: 2,
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              },
            },
          }}
        >
          <Typography
            sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: softAccent, textTransform: 'uppercase', mb: 1 }}
          >
            Autoplay interval
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#fff', mb: 1 }}>
            {(autoplayMs / 1000).toFixed(0)} seconds per callout
          </Typography>
          <Slider
            size="small"
            value={autoplayMs / 1000}
            min={6}
            max={30}
            step={1}
            onChange={(_, v) => setAutoplayMs((v as number) * 1000)}
            sx={{
              color: accent,
              '& .MuiSlider-track': { bgcolor: accent },
              '& .MuiSlider-thumb': { bgcolor: '#fff' },
              '& .MuiSlider-rail': { bgcolor: alpha('#fff', 0.2) },
            }}
          />
          <Typography sx={{ fontSize: 10.5, color: alpha('#fff', 0.55), mt: 1 }}>
            Tour loops back to the start at the end.
          </Typography>
        </Menu>

        {/* Step picker menu */}
        <Menu
          anchorEl={pickerAnchor}
          open={Boolean(pickerAnchor)}
          onClose={() => setPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ zIndex: 2500 }}
          slotProps={{
            root: { sx: { zIndex: 2500 } },
            paper: {
              sx: {
                bgcolor: '#0A1612',
                color: '#fff',
                border: `1px solid ${alpha(softAccent, 0.3)}`,
                borderRadius: 1.5,
                maxHeight: 440,
                minWidth: 380,
                mt: 0.5,
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                '& .MuiMenuItem-root': { py: 0.75, px: 1.5 },
              },
            },
          }}
        >
          <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${alpha('#fff', 0.08)}` }}>
            <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 2, color: softAccent, textTransform: 'uppercase' }}>
              Jump to feature
            </Typography>
          </Box>
          {TOUR_STEPS.map((s, i) => {
            const isCurrent = i === stepIndex;
            return (
              <MenuItem
                key={s.id}
                onClick={() => { goToStep(i, 0); setPickerAnchor(null); }}
                sx={{
                  bgcolor: isCurrent ? alpha(accent, 0.18) : 'transparent',
                  '&:hover': { bgcolor: isCurrent ? alpha(accent, 0.24) : alpha('#fff', 0.06) },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      minWidth: 36,
                      height: 22,
                      borderRadius: 0.75,
                      bgcolor: accent,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 10.5,
                    }}
                  >
                    {s.number}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: isCurrent ? 700 : 500,
                        color: isCurrent ? softAccent : '#fff',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.title}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: alpha('#fff', 0.5) }}>
                      {s.view}
                    </Typography>
                  </Box>
                  {isCurrent && <Check size={14} color={accent} />}
                </Stack>
              </MenuItem>
            );
          })}
          <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />
          <MenuItem
            onClick={() => { restart(); setPickerAnchor(null); }}
            sx={{ '&:hover': { bgcolor: alpha('#fff', 0.06) } }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <RotateCcw size={14} color={alpha('#fff', 0.7) as string} />
              <Typography sx={{ fontSize: 12.5, color: alpha('#fff', 0.85) }}>Restart from beginning</Typography>
            </Stack>
          </MenuItem>
        </Menu>
      </Box>
    </Portal>
  );
}
